import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Select, Spinner } from "flowbite-react";
import {
  HiArrowPath,
  HiBuildingOffice2,
  HiClipboardDocumentList,
  HiFunnel,
  HiMapPin,
  HiMiniArrowDownTray,
  HiSparkles,
} from "react-icons/hi2";
import { HiSearch, HiChevronUp, HiChevronDown } from "react-icons/hi";
import axios from "../config/axiosInstance";
import { CRM, INTEGRATIONS, LEADS } from "../config/apiConfig";
import { maskCNPJ, maskPhone } from "../utils/masks";
import AutoCompleteInput from "../components/inputs/Autocomplete";
import { FaLocationArrow } from "react-icons/fa6";

const UF_OPTIONS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

const PORTE_OPTIONS = [
  { value: "", label: "Qualquer porte" },
  { value: "Microempresa (ME)", label: "Microempresa (ME)" },
  {
    value: "Empresa de Pequeno Porte (EPP)",
    label: "Empresa de Pequeno Porte (EPP)",
  },
  { value: "Demais", label: "Demais Portes" },
];

const SITUACAO_OPTIONS = [
  { value: "", label: "Todas as situações" },
  { value: "Ativa", label: "Ativa" },
  { value: "Inapta", label: "Inapta" },
  { value: "Baixada", label: "Baixada" },
  { value: "Suspensa", label: "Suspensa" },
];

// Pequeno fallback para quando o fetch do IBGE falhar
const CNAE_FALLBACK = [
  { value: "6920601", label: "6920601 — Atividades de contabilidade" },
  {
    value: "6920602",
    label:
      "6920602 — Atividades de consultoria e auditoria contábil e tributária",
  },
  {
    value: "7020400",
    label: "7020400 — Atividades de consultoria em gestão empresarial",
  },
  {
    value: "8211300",
    label: "8211300 — Serviços combinados de escritório e apoio administrativo",
  },
  {
    value: "8219999",
    label:
      "8219999 — Preparação de documentos e serviços especializados de apoio administrativo",
  },
  {
    value: "8299799",
    label:
      "8299799 — Outras atividades de serviços prestados principalmente às empresas",
  },
  {
    value: "8599699",
    label:
      "8599699 — Outras atividades de ensino não especificadas anteriormente",
  },
  {
    value: "6619302",
    label: "6619302 — Correspondentes de instituições financeiras",
  },
];

const createInitialFilters = () => ({
  uf: "",
  cities: [],
  porte_empresa: "",
  cnaes: [],
  situacao_cadastral: "Ativa",
  minMonths: "",
  limit: 10,
  razaoSocial: "",
});

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const [y, m, d] = String(iso).split("-").map(Number);
    const parsed = new Date(y, (m || 1) - 1, d || 1);
    if (Number.isNaN(parsed.getTime())) return "—";
    return parsed.toLocaleDateString("pt-BR");
  } catch {
    return "—";
  }
};

const formatPhones = (telefones) => {
  const list = telefones?.list || [];
  return list
    .map((t) => t?.element)
    .filter(Boolean)
    .map((t) => maskPhone(`${t.ddd || ""}${t.numero || ""}`))
    .filter(Boolean);
};

const formatAddress = (empresa) => {
  const parts = [
    empresa?.tipo_logradouro,
    empresa?.logradouro,
    empresa?.numero,
    empresa?.bairro,
  ]
    .filter(Boolean)
    .join(" ");
  const cityState = [empresa?.municipio, empresa?.uf].filter(Boolean).join("/");
  return [parts, cityState].filter(Boolean).join(" • ");
};

const onlyDigits = (v = "") => String(v || "").replace(/\D/g, "");

const extractPhonesDigits = (telefones) =>
  (telefones?.list || [])
    .map((t) => {
      const base = t?.element || t || {};
      const raw = `${base.ddd || ""}${base.numero || base.telefone || ""}`;
      return onlyDigits(raw);
    })
    .filter(Boolean);

const normalizeCityForQuery = (value) => {
  if (!value) return "";
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
};

const stringsFromValue = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const dedupeStrings = (list = []) =>
  Array.from(
    new Set(
      (list || [])
        .map((item) => (item ? String(item).trim() : ""))
        .filter(Boolean)
    )
  );

const buildSelectionSummary = (count, singular, plural) => {
  if (!count) return "";
  const label = count === 1 ? singular : plural;
  const ending = count === 1 ? "" : "s";
  return `${count} ${label} selecionado${ending}`;
};

const buildLeadPayload = (empresa) => {
  const phoneOptions = extractPhonesDigits(empresa?.telefones);
  const document = onlyDigits(empresa?.cnpj || "");
  const notes = [
    empresa?.porte_empresa && `Porte: ${empresa.porte_empresa}`,
    empresa?.situacao_cadastral &&
      `Situação: ${empresa.situacao_cadastral || ""}`,
    empresa?.cnae_principal && `CNAE: ${empresa.cnae_principal}`,
    empresa?.municipio && empresa?.uf
      ? `Local: ${empresa.municipio}/${empresa.uf}`
      : null,
  ]
    .filter(Boolean)
    .join(" | ");

  return {
    name:
      empresa?.nome_fantasia ||
      empresa?.razao_social ||
      "Lead importado pelo gerador",
    companyName: empresa?.razao_social || empresa?.nome_fantasia || null,
    email: empresa?.email || null,
    phoneNumber: phoneOptions[0] || null,
    document: document || null,
    source: "lead-generator",
    notes: notes || null,
  };
};

export default function LeadGeneratorPage() {
  const [filters, setFilters] = useState(createInitialFilters);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState({ count: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastQuery, setLastQuery] = useState(null);
  const [cnaeOptions, setCnaeOptions] = useState([]);
  const [loadingCnae, setLoadingCnae] = useState(false);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [expandedCards, setExpandedCards] = useState({});
  const [preFunnel, setPreFunnel] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState("");
  const [selectedStageId, setSelectedStageId] = useState("");
  const [preFunnelError, setPreFunnelError] = useState("");
  const totalSelectedFilters =
    (filters.cities?.length || 0) +
    (filters.cnaes?.length || 0) +
    (filters.razaoSocial ? 1 : 0);

  const selectedFunnel = useMemo(
    () =>
      funnels.find((f) => String(f.id) === String(selectedFunnelId)) || null,
    [funnels, selectedFunnelId]
  );

  const selectedStage = useMemo(
    () =>
      selectedFunnel?.stages?.find(
        (s) => String(s.id) === String(selectedStageId)
      ) || null,
    [selectedFunnel, selectedStageId]
  );

  const updateField = (field, value) => {
    if (field === "cities" || field === "cnaes") {
      const normalized = dedupeStrings(stringsFromValue(value));
      setFilters((prev) => ({ ...prev, [field]: normalized }));
      return;
    }
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const buildParams = () => {
    const params = {};
    const selectedCities = Array.isArray(filters.cities)
      ? filters.cities
      : [];
    const normalizedCities = selectedCities
      .map((city) => normalizeCityForQuery(city))
      .filter(Boolean);
    if (normalizedCities.length) {
      params.cities = normalizedCities.join(",");
    }
    if (filters.uf) params.uf = filters.uf;
    if (filters.porte_empresa) params.porte_empresa = filters.porte_empresa;
    const selectedCnaes = Array.isArray(filters.cnaes)
      ? filters.cnaes
      : [];
    const normalizedCnaes = selectedCnaes
      .map((value) => (value ? String(value).trim() : ""))
      .filter(Boolean);
    if (normalizedCnaes.length) {
      params.cnaes = normalizedCnaes.join(",");
    }
    if (filters.situacao_cadastral)
      params.situacao_cadastral = filters.situacao_cadastral;
    const minMonths = Number(filters.minMonths);
    if (Number.isFinite(minMonths) && minMonths > 0) {
      params.minMonthsSinceStart = Math.floor(minMonths);
    }
    if (filters.limit) params.limit = Number(filters.limit) || 10;
    if (filters.razaoSocial?.trim()) {
      params.razaoSocial = filters.razaoSocial.trim();
    }
    return params;
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    setError("");
    const params = buildParams();
    const hasFilters = [
      "cities",
      "uf",
      "porte_empresa",
      "cnaes",
      "situacao_cadastral",
      "minMonthsSinceStart",
      "razaoSocial",
    ].some((key) => params[key]);
    if (!hasFilters) {
      setError(
        "Informe ao menos um filtro (cidade, UF, porte, CNAE, situação ou data)."
      );
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get(INTEGRATIONS.OPEN_CNPJ_COMPANIES, {
        params,
      });
      setResults(data?.data || []);
      setMeta({ count: data?.count || 0 });
      setLastQuery(params);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Erro ao buscar leads";
      setError(msg);
      setResults([]);
      setMeta({ count: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(createInitialFilters);
    setResults([]);
    setMeta({ count: 0 });
    setLastQuery(null);
    setError("");
    setCities([]);
  };

  const updatePreFunnelItem = (cnpj, updates) =>
    setPreFunnel((prev) =>
      prev.map((item) =>
        String(item.cnpj) === String(cnpj) ? { ...item, ...updates } : item
      )
    );

  const togglePreFunnel = (empresa) => {
    if (!empresa?.cnpj) return;
    setPreFunnelError("");
    setPreFunnel((prev) => {
      const exists = prev.find(
        (item) => String(item.cnpj) === String(empresa.cnpj)
      );
      if (exists) {
        return prev.filter(
          (item) => String(item.cnpj) !== String(empresa.cnpj)
        );
      }
      return [
        ...prev,
        {
          cnpj: empresa.cnpj,
          empresa,
          status: "pending",
          leadId: null,
          cardId: null,
          error: "",
        },
      ];
    });
  };

  const clearPreFunnel = () => {
    setPreFunnel([]);
    setPreFunnelError("");
  };

  const canImportEmpresa = (empresa) => {
    const payload = buildLeadPayload(empresa);
    return !!(payload.email || payload.phoneNumber);
  };

  const ensureLeadFromEmpresa = async (
    item,
    { statusOnStart = "creatingLead", statusOnSuccess = "leadCreated" } = {}
  ) => {
    if (!item?.empresa) throw new Error("Lead inválido para importação.");
    const payload = buildLeadPayload(item.empresa);
    if (!payload.email && !payload.phoneNumber) {
      const message = "Sem e-mail ou telefone para importar este lead.";
      updatePreFunnelItem(item.cnpj, { status: "error", error: message });
      throw new Error(message);
    }

    if (statusOnStart) {
      updatePreFunnelItem(item.cnpj, { status: statusOnStart, error: "" });
    }

    try {
      const res = await axios.post(`${LEADS.CREATE}`, payload);
      const created = res?.data?.data || res?.data;
      updatePreFunnelItem(item.cnpj, {
        leadId: created?.id,
        status: statusOnSuccess,
        error: "",
      });
      return created?.id;
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Erro ao criar lead.";
      updatePreFunnelItem(item.cnpj, { status: "error", error: msg });
      throw err;
    }
  };

  const handleCreateLeadFromPreFunnel = async (item) => {
    if (!item) return;
    if (item.leadId) {
      updatePreFunnelItem(item.cnpj, {
        status: item.cardId ? item.status : "leadCreated",
        error: item.cardId ? item.error : "",
      });
      return;
    }
    try {
      await ensureLeadFromEmpresa(item);
    } catch {
      // erro já tratado em ensureLeadFromEmpresa
    }
  };

  const handleCreateCardFromPreFunnel = async (item) => {
    if (!item) return;
    if (!selectedStage) {
      setPreFunnelError("Selecione um funil/etapa para criar cards.");
      updatePreFunnelItem(item.cnpj, {
        status: "error",
        error: "Selecione um funil/etapa para criar o card.",
      });
      return;
    }

    try {
      setPreFunnelError("");
      updatePreFunnelItem(item.cnpj, { status: "creatingCard", error: "" });

      const leadId =
        item.leadId ||
        (await ensureLeadFromEmpresa(item, {
          statusOnStart: "creatingCard",
          statusOnSuccess: "creatingCard",
        }));

      const res = await axios.post(`${CRM.CREATE_CARD}/${selectedStage.id}`, {
        title:
          item?.empresa?.nome_fantasia ||
          item?.empresa?.razao_social ||
          "Lead importado",
        leadId,
        priority: 1,
      });

      const createdCard = res?.data?.data || res?.data;
      updatePreFunnelItem(item.cnpj, {
        cardId: createdCard?.id,
        leadId,
        status: "cardCreated",
        error: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Erro ao criar card.";
      updatePreFunnelItem(item.cnpj, { status: "error", error: msg });
    }
  };

  /* Carrega tabela de CNAEs (IBGE) com fallback reduzido */
  useEffect(() => {
    let cancelled = false;
    async function fetchCnaes() {
      setLoadingCnae(true);
      try {
        const res = await fetch(
          "https://servicodados.ibge.gov.br/api/v2/cnae/subclasses"
        );
        if (!res.ok) throw new Error("Falha ao carregar CNAEs");
        const json = await res.json();
        const mapped =
          Array.isArray(json) && json.length
            ? json
                .map((item) => {
                  const rawId = String(item?.id || "");
                  const codeDigits = rawId.replace(/\D/g, "").padStart(7, "0"); // garante código completo
                  const desc = item?.descricao || "";
                  if (!codeDigits) return null;
                  return {
                    value: codeDigits,
                    label: `${rawId} — ${desc}`,
                  };
                })
                .filter(Boolean)
            : [];
        if (!cancelled) {
          setCnaeOptions(mapped.length ? mapped : CNAE_FALLBACK);
        }
      } catch (err) {
        if (!cancelled) {
          setCnaeOptions(CNAE_FALLBACK);
        }
      } finally {
        if (!cancelled) setLoadingCnae(false);
      }
    }
    fetchCnaes();
    return () => {
      cancelled = true;
    };
  }, []);

  // Carrega cidades ao trocar UF
  useEffect(() => {
    const uf = filters.uf;
    setFilters((prev) => ({ ...prev, cities: [] }));
    setCities([]);
    if (!uf) return;
    let cancelled = false;
    async function fetchCities() {
      setLoadingCities(true);
      try {
        const res = await fetch(
          `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/distritos`
        );
        if (!res.ok) throw new Error("Falha ao carregar cidades");
        const json = await res.json();
        const mapped =
          Array.isArray(json) && json.length
            ? Array.from(
                new Map(
                  json.map((item) => [
                    item?.municipio?.nome || item?.nome,
                    {
                      value: item?.municipio?.nome || item?.nome,
                      label: item?.municipio?.nome || item?.nome,
                    },
                  ])
                ).values()
              ).sort((a, b) =>
                a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" })
              )
            : [];
        if (!cancelled) setCities(mapped);
      } catch (err) {
        if (!cancelled) setCities([]);
      } finally {
        if (!cancelled) setLoadingCities(false);
      }
    }
    fetchCities();
    return () => {
      cancelled = true;
    };
  }, [filters.uf]);

  // Pré-carrega funis e mantém etapa selecionada para criação de cards
  useEffect(() => {
    let cancelled = false;
    async function fetchFunnels() {
      try {
        const res = await axios.get(CRM.FUNNELS);
        const list = res?.data?.data || [];
        if (cancelled) return;

        setFunnels(list);

        if (list.length) {
          const chosen =
            list.find((f) => String(f.id) === String(selectedFunnelId)) ||
            list[0];
          setSelectedFunnelId(String(chosen.id));
          const firstStage =
            chosen.stages?.[0] ||
            list.find((f) => (f.stages || []).length > 0)?.stages?.[0];
          if (firstStage) {
            setSelectedStageId(String(firstStage.id));
          }
        } else {
          setSelectedStageId("");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Erro ao carregar funis", err);
        }
      }
    }
    fetchFunnels();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const funnel =
      funnels.find((f) => String(f.id) === String(selectedFunnelId)) || null;
    if (!funnel) {
      if (selectedStageId) setSelectedStageId("");
      return;
    }
    const stages = funnel.stages || [];
    const exists = stages.some((s) => String(s.id) === String(selectedStageId));
    if (!exists) {
      setSelectedStageId(stages[0] ? String(stages[0].id) : "");
    }
  }, [selectedFunnelId, funnels, selectedStageId]);

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleSearch}
        className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm space-y-4"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-base font-semibold text-gray-700">
            <HiBuildingOffice2 className="text-indigo-500" />
            Filtros de busca
          </div>
          <Button
            color="light"
            type="button"
            size="xs"
            onClick={() => setFiltersOpen((prev) => !prev)}
          >
            {filtersOpen ? (
              <HiChevronUp className="mr-2 h-4 w-4" />
            ) : (
              <HiChevronDown className="mr-2 h-4 w-4" />
            )}
            {filtersOpen ? "Recolher" : "Expandir"}
          </Button>
        </div>

        {filtersOpen && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="space-y-1 flex flex-col">
                <label className="text-sm font-semibold text-gray-700">
                  UF
                </label>
                <select
                  value={filters.uf}
                  onChange={(e) => updateField("uf", e.target.value)}
                  className="border-gray-300 py-3 rounded-md text-sm"
                >
                  <option value="">Todas</option>
                  {UF_OPTIONS.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <AutoCompleteInput
                  options={cities}
                  value={filters.cities}
                  onChange={(opt) =>
                    updateField(
                      "cities",
                      Array.isArray(opt) ? opt.map((item) => item.value) : []
                    )
                  }
                  isLoading={loadingCities}
                  name="city"
                  label="Cidade"
                  isClearable
                  disabled={!filters.uf}
                  className="w-full mt-5"
                  noOptionsMessage={
                    !filters.uf
                      ? "Selecione uma UF"
                      : loadingCities
                      ? "Carregando..."
                      : "Nenhuma cidade encontrada"
                  }
                  isMulti
                  hideMultiValue
                  closeMenuOnSelect
                  summaryLabel={buildSelectionSummary(
                    filters.cities?.length || 0,
                    "cidade",
                    "cidades"
                  )}
                />
              </div>

              <div className="space-y-1 flex flex-col">
                <label className="text-sm font-semibold text-gray-700">
                  Razão social
                </label>
                <input
                  type="text"
                  value={filters.razaoSocial}
                  onChange={(e) => updateField("razaoSocial", e.target.value)}
                  className="border-gray-300 py-3 rounded-md text-sm"
                  placeholder="Pesquisar por razão social"
                />
              </div>

              <div className="space-y-1 flex flex-col">
                <label className="text-sm font-semibold text-gray-700">
                  Porte
                </label>
                <select
                  value={filters.porte_empresa}
                  onChange={(e) => updateField("porte_empresa", e.target.value)}
                  className="border-gray-300 py-3 rounded-md text-sm"
                >
                  {PORTE_OPTIONS.map((p) => (
                    <option key={p.value || "all"} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <AutoCompleteInput
                  options={cnaeOptions}
                  value={filters.cnaes}
                  onChange={(opt) =>
                    updateField(
                      "cnaes",
                      Array.isArray(opt) ? opt.map((item) => item.value) : []
                    )
                  }
                  isLoading={loadingCnae}
                  name="cnae"
                  label="CNAE principal"
                  isClearable
                  className="w-full mt-5"
                  isMulti
                  hideMultiValue
                  closeMenuOnSelect
                  summaryLabel={buildSelectionSummary(
                    filters.cnaes?.length || 0,
                    "CNAE",
                    "CNAEs"
                  )}
                />
              </div>

              <div className="space-y-1 flex flex-col">
                <label className="text-sm font-semibold text-gray-700">
                  Situação cadastral
                </label>
                <select
                  className="border-gray-300 py-3 rounded-md text-sm"
                  value={filters.situacao_cadastral}
                  onChange={(e) =>
                    updateField("situacao_cadastral", e.target.value)
                  }
                >
                  {SITUACAO_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1 flex flex-col">
                <label className="text-sm font-semibold text-gray-700">
                  Mínimo de meses de abertura
                </label>
                <input
                  type="number"
                  min={0}
                  max={1200}
                  value={filters.minMonths}
                  onChange={(e) => updateField("minMonths", e.target.value)}
                  helperText="Filtra empresas abertas por pelo menos N meses"
                  className="border-gray-300 py-3 rounded-md text-sm"
                />
              </div>

              <div className="space-y-1 flex flex-col">
                <label className="text-sm font-semibold text-gray-700">
                  Limite de resultados
                </label>
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={filters.limit}
                  onChange={(e) => updateField("limit", e.target.value)}
                  helperText="Quantidade máxima de leads que serão gerados"
                  className="border-gray-300 py-3 rounded-md text-sm"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
              <div className="flex gap-2">
                <Button color="light" type="button" onClick={handleReset}>
                  <HiArrowPath className="mr-2 h-4 w-4" />
                  Limpar
                </Button>
                <Button
                  type="submit"
                  className="bg-orange-500"
                  disabled={loading}
                >
                  <div className="flex gap-2 items-center">
                    {loading ? (
                      <Spinner size={"sm"} />
                    ) : (
                      <HiSearch className="h-4 w-4" />
                    )}

                    <span>{loading ? "Gerando Leads..." : "Gerar Leads"}</span>
                  </div>
                </Button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </form>

      {totalSelectedFilters > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm space-y-3">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Filtros aplicados</span>
            <span className="font-semibold text-gray-600">
              {totalSelectedFilters} filtro
              {totalSelectedFilters > 1 && "s"}
            </span>
          </div>
          {Array.isArray(filters.cities) && filters.cities.length > 0 && (
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">
                Cidades selecionadas
              </div>
              <div className="flex flex-wrap gap-2">
                {filters.cities.map((city) => (
                  <span
                    key={`city-${city}`}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-indigo-50/70 px-3 py-1 text-xs font-semibold text-indigo-700"
                  >
                    {city}
                    <button
                      type="button"
                      onClick={() =>
                        updateField(
                          "cities",
                          filters.cities.filter((value) => value !== city)
                        )
                      }
                      className="rounded-full text-indigo-500 hover:text-indigo-800"
                      aria-label={`Remover ${city}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(filters.cnaes) && filters.cnaes.length > 0 && (
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">
                CNAEs selecionados
              </div>
              <div className="flex flex-wrap gap-2">
                {filters.cnaes.map((code) => {
                  const option = cnaeOptions.find(
                    (opt) => String(opt.value) === String(code)
                  );
                  return (
                    <span
                      key={`cnae-${code}`}
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-amber-50/70 px-3 py-1 text-xs font-semibold text-amber-700 truncate max-w-[360px]"
                    >
                      {option?.label || code}
                      <button
                        type="button"
                        onClick={() =>
                          updateField(
                            "cnaes",
                            filters.cnaes.filter((value) => value !== code)
                          )
                        }
                        className="rounded-full text-amber-600 hover:text-amber-800"
                        aria-label={`Remover ${code}`}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {filters.razaoSocial && (
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">
                Razão social pesquisada
              </div>
              <div className="flex items-center gap-2 flex-wrap border border-gray-200 bg-amber-50/70 px-3 py-0 w-fit rounded-full">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 truncate">
                  {filters.razaoSocial}
                </span>
                <button
                  type="button"
                  onClick={() => updateField("razaoSocial", "")}
                  className="rounded-full text-amber-600 hover:text-amber-800"
                  aria-label="Remover razão social"
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {preFunnel.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <HiClipboardDocumentList className="text-indigo-500" />
              Pré-funil de importação
              <Badge color="info">{preFunnel.length}</Badge>
            </div>
            <div className="flex items-center gap-3">
              {preFunnelError && (
                <span className="text-xs text-red-600">{preFunnelError}</span>
              )}
              <Button size="xs" color="light" onClick={clearPreFunnel}>
                Limpar seleção
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-4 py-3 border-b border-gray-100">
            <div className="space-y-1">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">
                Funil destino
              </p>
              <Select
                value={selectedFunnelId}
                onChange={(e) => setSelectedFunnelId(e.target.value)}
              >
                {funnels.length === 0 && (
                  <option value="">Nenhum funil disponível</option>
                )}
                {funnels.map((funnel) => (
                  <option key={funnel.id} value={funnel.id}>
                    {funnel.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">
                Etapa
              </p>
              <Select
                value={selectedStageId}
                onChange={(e) => setSelectedStageId(e.target.value)}
                disabled={
                  !selectedFunnel || !(selectedFunnel?.stages || []).length
                }
              >
                {(selectedFunnel?.stages || []).map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </Select>
              {!selectedFunnel?.stages?.length && (
                <p className="text-[11px] text-gray-500">
                  Adicione etapas ao funil selecionado.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">
                Destino selecionado
              </p>
              <div className="text-xs text-gray-700">
                {selectedFunnel ? (
                  <>
                    <span className="font-semibold">{selectedFunnel.name}</span>
                    {selectedStage && ` • ${selectedStage.name}`}
                  </>
                ) : (
                  "Nenhum funil disponível"
                )}
              </div>
            </div>
          </div>

          <div className="divide-y">
            {preFunnel.map((item) => {
              const status = item.status || "pending";
              const busy =
                status === "creatingLead" || status === "creatingCard";
              const importable = canImportEmpresa(item.empresa);
              return (
                <div
                  key={item.cnpj}
                  className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex-1 min-w-0 space-y-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {item?.empresa?.nome_fantasia ||
                        item?.empresa?.razao_social ||
                        "Lead"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {maskCNPJ(onlyDigits(item.cnpj || ""))} •{" "}
                      {[item?.empresa?.municipio, item?.empresa?.uf]
                        .filter(Boolean)
                        .join("/")}
                    </p>
                    <div className="flex flex-wrap gap-2 text-[11px] text-gray-600">
                      {item?.empresa?.cnae_principal && (
                        <Badge color="gray">
                          CNAE {item?.empresa?.cnae_principal}
                        </Badge>
                      )}
                      {item?.empresa?.porte_empresa && (
                        <Badge color="info">{item.empresa.porte_empresa}</Badge>
                      )}
                      {status === "pending" && (
                        <Badge color="gray">Pendente</Badge>
                      )}
                      {status === "creatingLead" && (
                        <Badge color="info">
                          <div className="flex items-center gap-1">
                            <Spinner size="xs" />
                            Criando lead
                          </div>
                        </Badge>
                      )}
                      {status === "creatingCard" && (
                        <Badge color="info">
                          <div className="flex items-center gap-1">
                            <Spinner size="xs" />
                            Criando card
                          </div>
                        </Badge>
                      )}
                      {status === "leadCreated" && (
                        <Badge color="success">
                          Lead criado{" "}
                          {item.leadId
                            ? `#${String(item.leadId).slice(0, 8)}`
                            : ""}
                        </Badge>
                      )}
                      {status === "cardCreated" && (
                        <Badge color="success">
                          Card criado{" "}
                          {item.cardId
                            ? `#${String(item.cardId).slice(0, 8)}`
                            : ""}
                        </Badge>
                      )}
                      {status === "error" && !item.error && (
                        <Badge color="failure">Erro</Badge>
                      )}
                    </div>
                    {!importable && (
                      <p className="text-[12px] text-orange-600">
                        Sem e-mail ou telefone. Complete os dados antes de
                        importar.
                      </p>
                    )}
                    {item.error && (
                      <p className="text-[12px] text-red-600">{item.error}</p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="xs"
                      color="info"
                      onClick={() => handleCreateLeadFromPreFunnel(item)}
                      disabled={busy || !importable}
                    >
                      <div className="flex items-center gap-1">
                        {status === "creatingLead" ? (
                          <Spinner size="sm" />
                        ) : (
                          <HiSparkles className="h-4 w-4" />
                        )}
                        <span>Criar lead</span>
                      </div>
                    </Button>
                    <Button
                      size="xs"
                      color="success"
                      onClick={() => handleCreateCardFromPreFunnel(item)}
                      disabled={
                        busy || !importable || !selectedStage || !selectedFunnel
                      }
                    >
                      <div className="flex items-center gap-1">
                        {status === "creatingCard" ? (
                          <Spinner size="sm" />
                        ) : (
                          <HiFunnel className="h-4 w-4" />
                        )}
                        <span>Criar card</span>
                      </div>
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => togglePreFunnel(item.empresa)}
                      disabled={busy}
                    >
                      Remover
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <HiMapPin className="text-emerald-500" />
            Resultados
            <Badge color="gray">{meta.count || results.length}</Badge>
          </div>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Spinner size="sm" />
              Buscando leads...
            </div>
          )}
        </div>

        {!loading && results.length === 0 && (
          <div className="p-6 text-sm text-gray-500">
            Nenhum lead listado ainda. Ajuste os filtros e clique em "Gerar
            leads".
          </div>
        )}

        {results.length > 0 && (
          <div className="p-4 grid grid-cols-1 gap-4 bg-gray-100">
            {results.map((empresa) => {
              const phones = formatPhones(empresa?.telefones);
              const address = formatAddress(empresa);
              const isOpen = expandedCards[empresa.cnpj];
              const inPreFunnel = preFunnel.some(
                (item) => String(item.cnpj) === String(empresa.cnpj)
              );
              return (
                <div
                  key={empresa.cnpj}
                  className="rounded-xl border border-gray-200 bg-gradient-to-br from-white via-gray-50 to-white shadow-sm hover:shadow-md transition-all flex flex-col"
                >
                  <div className="flex items-start gap-3 border-b border-gray-100 px-4 py-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-semibold">
                      <HiBuildingOffice2 />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {empresa.nome_fantasia || empresa.razao_social}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {empresa.razao_social}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="xs"
                        color={inPreFunnel ? "failure" : "success"}
                        onClick={() => togglePreFunnel(empresa)}
                        title={
                          inPreFunnel
                            ? "Remover do pré-funil"
                            : "Enviar para o pré-funil"
                        }
                      >
                        <div className="flex items-center gap-1">
                          <HiMiniArrowDownTray className="h-4 w-4" />
                          <span>
                            {inPreFunnel ? "No pré-funil" : "Pré-funil"}
                          </span>
                        </div>
                      </Button>
                      <Button
                        size="xs"
                        color="light"
                        onClick={() =>
                          setExpandedCards((prev) => ({
                            ...prev,
                            [empresa.cnpj]: !prev[empresa.cnpj],
                          }))
                        }
                      >
                        {isOpen ? "Ocultar" : "Detalhes"}
                      </Button>
                    </div>
                  </div>

                  <div className="px-4 py-3 space-y-2">
                    <div className="flex flex-wrap gap-4">
                      {address ? (
                        <div className="flex items-center gap-2">
                          <FaLocationArrow className="text-sm" />
                          <p className="text-[12px] text-gray-600 line-clamp-2">
                            {address}
                          </p>
                        </div>
                      ) : (
                        <p className="text-[12px] text-gray-500">—</p>
                      )}

                      <p className="text-[12px] text-gray-600">
                        <span className="font-semibold">CNAE:</span>{" "}
                        {empresa.cnae_principal || "—"}
                      </p>
                      <p className="text-[12px] text-gray-600">
                        <span className="font-semibold">
                          {" "}
                          Data de fundação:{" "}
                        </span>
                        {formatDate(empresa?.data_inicio_atividade?.value)}
                      </p>
                      <p className="text-[12px] font-mono text-gray-600">
                        <span className="font-semibold">CNPJ:</span>{" "}
                        {maskCNPJ(String(empresa.cnpj || ""))}
                      </p>
                    </div>

                    {isOpen && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 text-xs text-gray-600 space-y-2 border-t border-gray-200 pt-3">
                        <div className="grid grid-cols-1 gap-2">
                          <div className="space-y-1">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400">
                              Perfil
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {empresa.porte_empresa && (
                                <Badge color="info">
                                  {empresa.porte_empresa}
                                </Badge>
                              )}
                              {empresa.situacao_cadastral && (
                                <Badge color="success">
                                  {empresa.situacao_cadastral}
                                </Badge>
                              )}
                            </div>
                          </div>

                          <div className="space-y-1">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400">
                              Contatos
                            </p>
                            <p className="text-xs text-gray-700 break-all">
                              Email: {empresa.email || "—"}
                            </p>
                            {phones.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {phones.map((p) => (
                                  <div
                                    key={p}
                                    className="text-xs bg-gray-100 px-2 py-1 rounded"
                                  >
                                    {p}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500">
                                Telefone não informado
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid text-xs text-gray-600 space-y-2">
                          <div className="rounded-lg bg-gray-100/60 ">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400">
                              Capital social
                            </p>
                            <p className="font-semibold text-gray-800">
                              {Number(
                                empresa.capital_social || 0
                              ).toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                                minimumFractionDigits: 0,
                              })}
                            </p>
                          </div>
                          <div className="rounded-lg bg-gray-100/60 ">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400">
                              Sócios
                            </p>
                            <p className="font-semibold text-gray-800">
                              {empresa?.socios?.list?.length || 0}
                            </p>
                          </div>
                        </div>
                        <div className="rounded-lg bg-gray-100/60 ">
                          <p className="text-[11px] uppercase tracking-wide text-gray-400">
                            CNAEs secundários
                          </p>
                          <p className="text-[11px] text-gray-700">
                            {(empresa?.cnaes_secundarios?.list || [])
                              .map((c) => c?.element)
                              .filter(Boolean)
                              .join(", ") || "—"}
                          </p>
                        </div>
                        <div className="rounded-lg bg-gray-100/60 ">
                          <p className="text-[11px] uppercase tracking-wide text-gray-400">
                            Natureza jurídica
                          </p>
                          <p className="text-[11px] text-gray-700">
                            {empresa.natureza_juridica || "—"}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
